import type { ScanReviewGroup } from "@hobbyshopify/backend-client";
import type { Result } from "operation-result.js";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import {
  confirmGroup,
  confirmGroups,
  loadReviewQueue,
  pickGroupCandidate,
  rejectGroup,
} from "../../core/ops/review-ops";
import { errorText } from "../../core/primitives/errors";
import { batchEntry, reviewEntry, reviewQuantity } from "../../core/primitives/review-row";
import { CONFIRM_ALL_KEY, DraftsActions, DraftsState } from "./drafts-model";

export type DraftsDeps = Pick<
  AppDeps,
  "client" | "authHandler" | "storeRepository" | "providersRepository" | "reviewQueueRepository" | "bulkScanQueue"
>;

export interface DraftsParams {
  /** `?bulk=1`: opened straight into bulk mode. */
  readonly bulkOpen: boolean;
}

export interface DraftsRouting {
  reviewScan(scanId: string): void;
  /** Keeps the URL honest so a refresh (or a shared link) reopens the same view. */
  setBulkOpen(open: boolean): void;
}

/**
 * The review queue: scans waiting for a person, and where bulk scanning happens.
 *
 * Nothing a scan produced is in the catalogue yet: confirming a group here is
 * what creates the card. The queue is grouped by the identity each scan read
 * as, so ten copies of one card are one row, one confirm and one lot of ten.
 * Each card being scanned sits at the top of that same list and is replaced by
 * its group when it lands.
 */
export class DraftsController extends SubtreeController implements DraftsActions {
  readonly state: DraftsState;

  constructor(
    readonly params: DraftsParams,
    readonly deps: DraftsDeps,
    readonly routing: DraftsRouting,
  ) {
    super();
    this.state = new DraftsState(params.bulkOpen);
    this.subtree.put(DraftsState, this.state);
    this.subtree.put(DraftsActions, this);

    this.sync(() => this.#loadIfNeeded(), [deps.storeRepository]);

    // The cached queue shows at once; a stale mark (a confirm elsewhere, a bulk
    // scan landing) fetches it again behind the rows.
    this.sync(() => {
      const repo = deps.reviewQueueRepository;
      this.state.groups.value = repo.groups;
      this.state.unrecognised.value = repo.unrecognised;
      this.state.status.value = repo.status;
      this.#loadIfNeeded();
    }, [deps.reviewQueueRepository]);

    this.sync(() => {
      const queue = deps.bulkScanQueue;
      this.state.bulkItems.value = queue.items;
      this.state.bulkCounts.value = queue.counts;
      this.state.bulkRunning.value = queue.running;
      this.state.bulkPaused.value = queue.paused;
    }, [deps.bulkScanQueue]);
  }

  toggleBulk = () => {
    const next = !this.state.bulkOpen.value;
    this.state.bulkOpen.value = next;
    this.routing.setBulkOpen(next);
  };

  confirm = (group: ScanReviewGroup) => {
    const entry = reviewEntry(group, this.state.drafts.value[group.key]);
    if (!entry) return Promise.resolve();
    return this.#act(group.key, "Confirm failed.", (shopId) => confirmGroup({ ...this.deps, shopId, entry }));
  };

  reject = (group: ScanReviewGroup) =>
    this.#act(group.key, "Reject failed.", (shopId) => rejectGroup({ ...this.deps, shopId, group }));

  confirmAllPriced = () => {
    const drafts = this.state.drafts.value;
    const entries = this.state.groups.value.flatMap((g) => batchEntry(g, drafts[g.key]) ?? []);
    if (entries.length === 0) return Promise.resolve();
    return this.#act(CONFIRM_ALL_KEY, "Confirm failed.", (shopId) => confirmGroups({ ...this.deps, shopId, entries }));
  };

  setPrice = (group: ScanReviewGroup, price: string) => {
    this.state.drafts.update((d) => ({ ...d, [group.key]: { ...d[group.key], price } }));
  };

  setQuantity = (group: ScanReviewGroup, quantity: number) => {
    const next = Math.max(0, Math.floor(quantity));
    if (next === reviewQuantity(group, this.state.drafts.value[group.key])) return;
    this.state.drafts.update((d) => ({ ...d, [group.key]: { ...d[group.key], quantity: next } }));
  };

  pick = async (group: ScanReviewGroup, productId: string) => {
    const shopId = this.deps.storeRepository.shopId;
    if (shopId === null || this.state.picking.value !== null) return;
    this.state.picking.value = { key: group.key, productId };
    this.state.actionError.value = null;
    try {
      const r = await pickGroupCandidate({ ...this.deps, shopId, group, productId });
      if (this.isDisposed) return;
      if (!r.ok) {
        this.state.actionError.value = errorText(r.errors) || "Could not switch the match.";
      }
    } finally {
      if (!this.isDisposed) this.state.picking.value = null;
    }
  };

  review = (group: ScanReviewGroup) => {
    this.routing.reviewScan(group.sample.id);
  };

  dismissError = () => {
    this.state.actionError.value = null;
  };

  retry = () => {
    const shopId = this.deps.storeRepository.shopId;
    if (shopId !== null)
      void loadReviewQueue({ ...this.deps, shopId, locationId: this.deps.storeRepository.locationId });
  };

  addFiles = (files: Iterable<File> | null) => {
    if (files) this.deps.bulkScanQueue.addFiles(files);
  };

  stopBulk = () => {
    this.deps.bulkScanQueue.stop();
  };

  resumeBulk = () => {
    this.deps.bulkScanQueue.resume();
  };

  retryItem = (id: string) => {
    this.deps.bulkScanQueue.retry(id);
  };

  removeItem = (id: string) => {
    this.deps.bulkScanQueue.remove(id);
  };

  async #act(key: string, fallback: string, op: (shopId: string) => Promise<Result<void, Error>>): Promise<void> {
    const shopId = this.deps.storeRepository.shopId;
    if (shopId === null || this.state.busyKey.value !== null) return;
    this.state.busyKey.value = key;
    this.state.actionError.value = null;
    try {
      const r = await op(shopId);
      if (this.isDisposed) return;
      this.state.actionError.value = r.ok ? null : errorText(r.errors) || fallback;
    } finally {
      this.state.busyKey.value = null;
    }
  }

  #loadIfNeeded(): void {
    const shopId = this.deps.storeRepository.shopId;
    if (shopId === null) return;
    const repo = this.deps.reviewQueueRepository;
    if (repo.status.kind === "idle" || repo.isStale) {
      void loadReviewQueue({ ...this.deps, shopId, locationId: this.deps.storeRepository.locationId });
    }
  }
}
